import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { UpdateProgress } from '../ota/types'
import { addProgressListener } from '../ota'

interface OTAProgressContextType {
  progress: UpdateProgress | null
  isUpdating: boolean
  clearProgress: () => void
}

const OTAProgressContext = createContext<OTAProgressContextType | undefined>(undefined)

interface OTAProgressProviderProps {
  children: ReactNode
}

export function OTAProgressProvider({ children }: OTAProgressProviderProps) {
  const [progress, setProgress] = useState<UpdateProgress | null>(null)

  useEffect(() => {
    const unsubscribe = addProgressListener((newProgress) => {
      console.log('[OTA Progress Context]', newProgress)
      setProgress(newProgress)

      // Clear progress after completion or error
      if (newProgress.stage === 'complete') {
        setTimeout(() => {
          setProgress(null)
        }, 3000) // Show completion for 3 seconds
      } else if (newProgress.stage === 'error') {
        setTimeout(() => {
          setProgress(null)
        }, 5000) // Show error for 5 seconds, then allow login to proceed
      }
    })

    return unsubscribe
  }, [])

  const isUpdating = progress !== null && progress.stage !== 'complete' && progress.stage !== 'error'

  const clearProgress = () => {
    setProgress(null)
  }

  const value: OTAProgressContextType = {
    progress,
    isUpdating,
    clearProgress,
  }

  return (
    <OTAProgressContext.Provider value={value}>
      {children}
    </OTAProgressContext.Provider>
  )
}

export function useOTAProgress(): OTAProgressContextType {
  const context = useContext(OTAProgressContext)
  if (context === undefined) {
    throw new Error('useOTAProgress must be used within an OTAProgressProvider')
  }
  return context
}